import { useCallback } from "react";
import { nakamaService, NakamaSession } from "../services/nakama";
import { Inventory, LoginCredentials, RegisterData } from "../types/auth";

const parseNakamaError = (error: unknown): Error => {
  if (!(error instanceof Error)) {
    return new Error("Error desconocido de conexión.");
  }

  try {
    const errorData = JSON.parse(error.message);
    if (
      errorData !== null &&
      typeof errorData === "object" &&
      "message" in errorData &&
      typeof errorData.message === "string"
    ) {
      return new Error(errorData.message);
    }
  } catch {
    // Si no es un JSON válido, conservamos el mensaje original
  }

  return error;
};

const createEmptyVault = (): Inventory => {
  const vault: Inventory = {};
  for (let row = 1; row <= 3; row++) {
    for (let col = 1; col <= 6; col++) {
      vault[`${col},${row}`] = null;
    }
  }
  return vault;
};

export const useAuth = () => {
  const readUserData = useCallback(
    async (session: NakamaSession, keyName: string) => {
      try {
        const result = await nakamaService.readStorageObjects(session, {
          objectIds: [
            {
              collection: "userdata",
              key: keyName,
              userId: session.user_id,
            },
          ],
        });

        if (result.objects.length > 0) {
          const rawValue = result.objects[0].value;

          if (typeof rawValue === "object" && rawValue !== null) {
            return rawValue;
          }

          if (typeof rawValue === "string") {
            try {
              return JSON.parse(rawValue);
            } catch (parseError: unknown) {
              console.warn(
                `Error parseando el string de ${keyName}:`,
                rawValue,
              );
              return null;
            }
          }
        }

        return null;
      } catch (error: unknown) {
        console.error(`Error de red al leer la key ${keyName}:`, error);
        throw parseNakamaError(error);
      }
    },
    [],
  );

  const checkExistingSession = useCallback(async () => {
    const token = localStorage.getItem("nakamaToken");
    const refreshToken = localStorage.getItem("nakamaRefreshToken");

    if (!token || !refreshToken) {
      return null;
    }

    try {
      const session = nakamaService.restoreSession(token, refreshToken);
      const account = await nakamaService.getAccount(session);

      const [profile, vault] = await Promise.all([
        readUserData(session, "profile"),
        readUserData(session, "vault"),
      ]);

      return {
        user: {
          userId: account.user.id,
          username: account.user.username,
          email: account.email ?? "", // Extraído correctamente de account
        },
        profile,
        vault: vault ?? createEmptyVault(),
      };
    } catch (error: unknown) {
      console.error("Error restoring session:", error);
      localStorage.removeItem("nakamaToken");
      localStorage.removeItem("nakamaRefreshToken");
      localStorage.removeItem("nakamaUserId");
      return null;
    }
  }, [readUserData]);

  const performLogin = useCallback(
    async (credentials: LoginCredentials) => {
      try {
        const session = await nakamaService.authenticateEmail(
          credentials.email,
          credentials.password,
          false,
          undefined,
        );

        localStorage.setItem("nakamaToken", session.token);
        localStorage.setItem("nakamaRefreshToken", session.refresh_token);
        localStorage.setItem("nakamaUserId", session.user_id);

        const account = await nakamaService.getAccount(session);
        const [profile, vault] = await Promise.all([
          readUserData(session, "profile"),
          readUserData(session, "vault"),
        ]);

        return {
          user: {
            userId: account.user.id,
            username: account.user.username,
            email: account.email ?? "",
          },
          profile,
          vault: vault ?? createEmptyVault(),
        };
      } catch (error: unknown) {
        throw parseNakamaError(error);
      }
    },
    [readUserData],
  );

  const performRegister = useCallback(async (data: RegisterData) => {
    if (data.password.length < 6) {
      throw new Error("La contraseña debe tener al menos 6 caracteres");
    }

    const birthDate = new Date(data.birthdate);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    const hasNotHadBirthdayThisYear =
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate());

    if (hasNotHadBirthdayThisYear) {
      age--;
    }

    if (age < 13) {
      throw new Error("Debes tener al menos 13 años para registrarte");
    }

    try {
      const session = await nakamaService.authenticateEmail(
        data.email,
        data.password,
        true,
        data.username,
      );

      const userProfile = {
        firstName: data.firstName,
        lastName: data.lastName,
        username: data.username,
        email: data.email,
        birthdate: data.birthdate,
        country: data.country,
        gender: data.gender,
        age,
        registeredAt: new Date().toISOString(),
      };

      await nakamaService.writeStorageObjects(session, [
        {
          collection: "userdata",
          key: "profile",
          value: userProfile,
          permissionRead: 1,
          permissionWrite: 0,
        },
        {
          collection: "userdata",
          key: "vault",
          value: createEmptyVault(),
          permissionRead: 1,
          permissionWrite: 0,
        },
      ]);
    } catch (error: unknown) {
      throw parseNakamaError(error);
    }
  }, []);

  const performLogout = useCallback(() => {
    localStorage.removeItem("nakamaToken");
    localStorage.removeItem("nakamaRefreshToken");
    localStorage.removeItem("nakamaUserId");
  }, []);

  return {
    checkExistingSession,
    performLogin,
    performRegister,
    performLogout,
    readUserData,
  };
};
